import React from 'react';
import { Copy, Link, QrCode, CalendarRange, Layers, Coins, Info } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';
import styles from './EventHeader.module.css';

const getEventStatus = (event) => {
    if (!event?.start_date && !event?.end_date) return null;
    const now = new Date();
    const start = event.start_date ? new Date(event.start_date) : null;
    const end = event.end_date ? new Date(event.end_date) : null;
    if (end && now > end) return { label: 'Ended', status: 'ended' };
    if (start && now < start) return { label: 'Upcoming', status: 'upcoming' };
    return { label: 'Active', status: 'active' };
};

export default function EventHeader({
    event,
    joinUrl,
    shareNote,
    activities = [],
    launchedActivityId = null,
    user = null,
    onCopyCode,
    onCopyLink,
    onShowQr,
}) {
    const totalInteractions = activities.length;
    const isLive = launchedActivityId != null;
    const isAdmin = user?.role === 'admin';
    const coins = user?.coins ?? 0;
    const hasSubscription = user?.subscriptionEndDate && new Date(user.subscriptionEndDate) >= new Date();
    const eventStatus = getEventStatus(event);

    const startDate = formatDate(event?.start_date);
    const endDate = formatDate(event?.end_date);

    return (
        <header className={styles.header}>
            <div className={styles.inner}>
                {/* ── Left: event info ── */}
                <div className={styles.left}>
                    {/* Title + code + status */}
                    <div className={styles.titleRow}>
                        <h1 className={styles.title}>{event?.name || 'Event'}</h1>
                        <span className={styles.codePill}>#{event?.code || '—'}</span>
                        {eventStatus && (
                            <span className={styles.statusBadge} data-status={eventStatus.status}>
                                {eventStatus.status === 'active' && (
                                    <span className={styles.activeDot} />
                                )}
                                {eventStatus.label}
                            </span>
                        )}
                    </div>

                    {/* Meta row: dates · interactions · live · coins */}
                    <div className={styles.metaRow}>
                        {(startDate || endDate) && (
                            <span className={styles.metaItem}>
                                <CalendarRange size={12} />
                                {startDate || '—'}
                                {endDate && endDate !== startDate && <> → {endDate}</>}
                            </span>
                        )}

                        {(startDate || endDate) && totalInteractions >= 0 && (
                            <span className={styles.metaDivider}>·</span>
                        )}

                        <span className={styles.metaItem}>
                            <Layers size={12} />
                            {totalInteractions} interaction{totalInteractions !== 1 ? 's' : ''}
                        </span>

                        {isLive && (
                            <>
                                <span className={styles.metaDivider}>·</span>
                                <span className={styles.metaItemLive}>
                                    <span className={styles.liveDot} />
                                    Live now
                                </span>
                            </>
                        )}

                        {isAdmin && (
                            <>
                                <span className={styles.metaDivider}>·</span>
                                <span className={styles.coinsChip}>
                                    <Coins size={11} />
                                    {hasSubscription ? 'Unlimited' : `${coins} coins`}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Right: action buttons ── */}
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => onCopyCode?.(event?.code ?? '', 'Event code')}
                    >
                        <Copy size={13} />
                        Copy code
                    </button>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => onCopyLink?.(joinUrl, 'Join link')}
                    >
                        <Link size={13} />
                        Copy link
                    </button>
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                        onClick={onShowQr}
                        disabled={!joinUrl}
                    >
                        <QrCode size={13} />
                        Show QR
                    </button>
                </div>
            </div>

            {shareNote && (
                <div className={styles.shareNote}>
                    <Info size={13} />
                    {shareNote}
                </div>
            )}
        </header>
    );
}
